/**
 * Rich text model: a string (newline = '\r', like Flash) plus contiguous format spans.
 * Mirrors Ruffle's FormatSpans closely enough for Flash-compatible text/htmlText behaviour.
 */
import { TextFormat } from './TextFormat';

/** Fully-resolved character format. */
export interface Fmt {
  font: string;
  size: number;
  color: number;
  bold: boolean;
  italic: boolean;
  underline: boolean;
  url: string;
  target: string;
  align: string;
  leftMargin: number;
  rightMargin: number;
  indent: number;
  leading: number;
  letterSpacing: number;
  kerning: boolean;
  blockIndent: number;
  bullet: boolean;
  tabStops: number[];
}

export function defaultFmt(): Fmt {
  return {
    font: 'Times New Roman',
    size: 12,
    color: 0,
    bold: false,
    italic: false,
    underline: false,
    url: '',
    target: '',
    align: 'left',
    leftMargin: 0,
    rightMargin: 0,
    indent: 0,
    leading: 0,
    letterSpacing: 0,
    kerning: false,
    blockIndent: 0,
    bullet: false,
    tabStops: [],
  };
}

export function cloneFmt(f: Fmt): Fmt {
  return { ...f, tabStops: f.tabStops.slice() };
}

export function fmtEquals(a: Fmt, b: Fmt): boolean {
  return (
    a.font === b.font &&
    a.size === b.size &&
    a.color === b.color &&
    a.bold === b.bold &&
    a.italic === b.italic &&
    a.underline === b.underline &&
    a.url === b.url &&
    a.target === b.target &&
    a.align === b.align &&
    a.leftMargin === b.leftMargin &&
    a.rightMargin === b.rightMargin &&
    a.indent === b.indent &&
    a.leading === b.leading &&
    a.letterSpacing === b.letterSpacing &&
    a.kerning === b.kerning &&
    a.blockIndent === b.blockIndent &&
    a.bullet === b.bullet &&
    a.tabStops.join(',') === b.tabStops.join(',')
  );
}

function num(v: any): number | null {
  if (v === null || v === undefined) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/** Apply the non-null fields of `tf` onto `base` (TextFormat -> Fmt). */
export function applyTextFormat(base: Fmt, tf: TextFormat): Fmt {
  const f = cloneFmt(base);
  if (tf.font !== null && tf.font !== undefined) f.font = String(tf.font);
  const size = num(tf.size);
  if (size !== null) f.size = size;
  const color = num(tf.color);
  if (color !== null) f.color = color >>> 0 & 0xffffff;
  if (tf.bold !== null && tf.bold !== undefined) f.bold = !!tf.bold;
  if (tf.italic !== null && tf.italic !== undefined) f.italic = !!tf.italic;
  if (tf.underline !== null && tf.underline !== undefined) f.underline = !!tf.underline;
  if (tf.url !== null && tf.url !== undefined) f.url = String(tf.url);
  if (tf.target !== null && tf.target !== undefined) f.target = String(tf.target);
  if (tf.align !== null && tf.align !== undefined) {
    const a = String(tf.align).toLowerCase();
    if (a === 'left' || a === 'right' || a === 'center' || a === 'justify') f.align = a;
  }
  const lm = num(tf.leftMargin);
  if (lm !== null) f.leftMargin = lm;
  const rm = num(tf.rightMargin);
  if (rm !== null) f.rightMargin = rm;
  const ind = num(tf.indent);
  if (ind !== null) f.indent = ind;
  const lead = num(tf.leading);
  if (lead !== null) f.leading = lead;
  const ls = num(tf.letterSpacing);
  if (ls !== null) f.letterSpacing = ls;
  if (tf.kerning !== null && tf.kerning !== undefined) f.kerning = !!tf.kerning;
  const bi = num(tf.blockIndent);
  if (bi !== null) f.blockIndent = bi;
  if (tf.bullet !== null && tf.bullet !== undefined) f.bullet = !!tf.bullet;
  if (Array.isArray(tf.tabStops)) f.tabStops = tf.tabStops.map(Number);
  return f;
}

export function fmtToTextFormat(f: Fmt): TextFormat {
  const tf = new TextFormat(f.font, f.size, f.color, f.bold, f.italic, f.underline, f.url, f.target, f.align, f.leftMargin, f.rightMargin, f.indent, f.leading);
  tf.letterSpacing = f.letterSpacing;
  tf.kerning = f.kerning;
  tf.blockIndent = f.blockIndent;
  tf.bullet = f.bullet;
  tf.tabStops = f.tabStops.slice();
  return tf;
}

export interface Span {
  len: number;
  fmt: Fmt;
}

export class FormatSpans {
  text: string;
  spans: Span[];

  constructor(text = '', spans: Span[] = []) {
    this.text = text;
    this.spans = spans;
  }

  static fromText(text: string, fmt: Fmt): FormatSpans {
    return new FormatSpans(text, text.length ? [{ len: text.length, fmt: cloneFmt(fmt) }] : []);
  }

  clone(): FormatSpans {
    return new FormatSpans(
      this.text,
      this.spans.map((s) => ({ len: s.len, fmt: cloneFmt(s.fmt) })),
    );
  }

  /** Merge equal neighbours and drop empty spans. */
  normalize(): void {
    const out: Span[] = [];
    for (const s of this.spans) {
      if (s.len <= 0) continue;
      const last = out[out.length - 1];
      if (last && fmtEquals(last.fmt, s.fmt)) last.len += s.len;
      else out.push({ len: s.len, fmt: s.fmt });
    }
    this.spans = out;
  }

  /** Iterate spans as [start, end, fmt]. */
  *iter(): Generator<[number, number, Fmt]> {
    let pos = 0;
    for (const s of this.spans) {
      yield [pos, pos + s.len, s.fmt];
      pos += s.len;
    }
  }

  /** Format of the character at `index` (clamped), or null when empty. */
  fmtAt(index: number): Fmt | null {
    let pos = 0;
    for (const s of this.spans) {
      if (index < pos + s.len) return s.fmt;
      pos += s.len;
    }
    return this.spans.length ? this.spans[this.spans.length - 1].fmt : null;
  }

  /** Split so that a span boundary exists at `index`. */
  private splitAt(index: number): void {
    let pos = 0;
    for (let i = 0; i < this.spans.length; i++) {
      const s = this.spans[i];
      if (index > pos && index < pos + s.len) {
        const a = { len: index - pos, fmt: s.fmt };
        const b = { len: pos + s.len - index, fmt: cloneFmt(s.fmt) };
        this.spans.splice(i, 1, a, b);
        return;
      }
      pos += s.len;
    }
  }

  setFormat(from: number, to: number, tf: TextFormat): void {
    from = Math.max(0, Math.min(from, this.text.length));
    to = Math.max(from, Math.min(to, this.text.length));
    if (from === to) return;
    this.splitAt(from);
    this.splitAt(to);
    let pos = 0;
    for (const s of this.spans) {
      if (pos >= from && pos + s.len <= to) s.fmt = applyTextFormat(s.fmt, tf);
      pos += s.len;
    }
    this.normalize();
  }

  /** TextFormat with fields equal over [from, to) (others null). */
  getFormat(from: number, to: number): TextFormat {
    const tf = new TextFormat();
    let first: Fmt | null = null;
    const keys: Array<keyof Fmt> = [
      'font',
      'size',
      'color',
      'bold',
      'italic',
      'underline',
      'url',
      'target',
      'align',
      'leftMargin',
      'rightMargin',
      'indent',
      'leading',
      'letterSpacing',
      'kerning',
      'blockIndent',
      'bullet',
    ];
    const mixed = new Set<string>();
    for (const [s, e, f] of this.iter()) {
      if (e <= from || s >= to) continue;
      if (!first) first = f;
      else for (const k of keys) if (first[k] !== f[k]) mixed.add(k);
    }
    if (!first) return tf;
    for (const k of keys) if (!mixed.has(k)) (tf as any)[k] = first[k];
    tf.tabStops = first.tabStops.slice();
    return tf;
  }

  /** Replace [from, to) with `str` formatted with `fmt`. */
  replace(from: number, to: number, str: string, fmt: Fmt): void {
    from = Math.max(0, Math.min(from, this.text.length));
    to = Math.max(from, Math.min(to, this.text.length));
    this.splitAt(from);
    this.splitAt(to);
    const out: Span[] = [];
    let pos = 0;
    let inserted = false;
    for (const s of this.spans) {
      const end = pos + s.len;
      if (end <= from) out.push(s);
      else if (pos >= to) {
        if (!inserted) {
          if (str.length) out.push({ len: str.length, fmt: cloneFmt(fmt) });
          inserted = true;
        }
        out.push(s);
      }
      pos = end;
    }
    if (!inserted && str.length) out.push({ len: str.length, fmt: cloneFmt(fmt) });
    this.spans = out;
    this.text = this.text.slice(0, from) + str + this.text.slice(to);
    this.normalize();
  }
}
